import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Checkbox,
  Collapse,
  Card,
  CardContent
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailIcon,
  Info as InfoIcon,
  CheckCircle,
  Cancel,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { questionBankService } from '../services/questionBankService';
import { QuestionForm, ConclusionForm } from '../components/QuestionEditor';
import { flowService } from '../services/flowService';

export const QuestionBank = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConclusionDialog, setOpenConclusionDialog] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [activeListTab, setActiveListTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('id');
  const [orderDirection, setOrderDirection] = useState('asc');
  const [formData, setFormData] = useState({
    text: '',
    options: '',
    is_final: false,
    final_message: '',
    article_reference: '',
    dynamic_text: '',
    is_success: null,
    newOption: ''
  });
  const [activeTab, setActiveTab] = useState(0);
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState('');
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  useEffect(() => {
    fetchQuestions();
    fetchFlows();
  }, []);

  useEffect(() => {
    fetchQuestions(selectedFlowId || null);
  }, [selectedFlowId]);

  const fetchFlows = async () => {
    try {
      const data = await flowService.list();
      
      // Buscar contagem de questões por fluxo
      const questionCounts = await questionBankService.getQuestionCountsByFlow();
      
      // Adicionar contagem de questões a cada fluxo
      const flowsWithQuestionCount = data.map(flow => ({
        ...flow,
        questionCount: questionCounts[flow.id] || 0
      }));
      
      // Ordenar fluxos alfabeticamente pelo nome
      const sortedFlows = [...flowsWithQuestionCount].sort((a, b) => {
        if (!a.name) return 1;
        if (!b.name) return -1;
        return a.name.localeCompare(b.name, 'pt-BR');
      });
      
      console.log('Fluxos ordenados alfabeticamente:', sortedFlows);
      setFlows(sortedFlows);
    } catch (error) {
      console.error('Erro ao buscar fluxos:', error);
      if (showNotification) {
        showNotification('Erro ao carregar fluxos', 'error');
      }
    }
  };

  const fetchQuestions = async (flowId = null) => {
    try {
      setLoading(true);
      const data = await questionBankService.list(flowId);
      
      // Ordenar questões alfabeticamente pelo título
      const sortedQuestions = [...data].sort((a, b) => {
        if (!a.title) return 1;
        if (!b.title) return -1;
        return a.title.localeCompare(b.title, 'pt-BR');
      });
      
      console.log('Questões ordenadas alfabeticamente:', sortedQuestions);
      setQuestions(sortedQuestions);
    } catch (error) {
      console.error('Erro ao buscar questões:', error);
      if (showNotification) {
        showNotification('Erro ao carregar o banco de questões', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (question = null) => {
    if (question?.is_final) {
      setCurrentQuestion(question);
        setOpenConclusionDialog(true);
    } else {
        setCurrentQuestion(question);
        setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOpenConclusionDialog(false);
    setCurrentQuestion(null);
  };

  const handleTabChange = (event, newValue) => {
    // Atualizar o estado is_final com base na aba selecionada
    setFormData({
      ...formData,
      is_final: newValue === 1
    });
    setActiveTab(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'is_final' ? checked : value
    });
  };

  const handleSubmit = async (formData) => {
    try {
        // Preparar os dados antes de enviar
        const dataToSubmit = {
        ...formData,
            dynamic_text: formData.dynamic_text || null,
            options: Array.isArray(formData.options) ? formData.options : []
        };

      let response;
        if (currentQuestion?.id) {
            response = await questionBankService.update(currentQuestion.id, dataToSubmit);
        if (showNotification) {
                showNotification('Item atualizado com sucesso', 'success');
        }
      } else {
            response = await questionBankService.create(dataToSubmit);
        if (showNotification) {
                showNotification(formData.is_final ? 'Conclusão criada com sucesso' : 'Questão criada com sucesso', 'success');
        }
      }

      fetchQuestions();
      handleCloseDialog();
    } catch (error) {
        console.error('Erro ao salvar:', error);
      if (showNotification) {
            showNotification(`Erro ao salvar ${formData.is_final ? 'conclusão' : 'questão'}`, 'error');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta questão?')) {
      try {
        await questionBankService.delete(id);
        showNotification('Questão excluída com sucesso', 'success');
        fetchQuestions();
      } catch (error) {
        console.error('Erro ao excluir questão:', error);
        if (error.response && error.response.status === 400) {
          showNotification('Não é possível excluir esta questão pois ela está em uso em um ou mais fluxos', 'error');
        } else {
          showNotification('Erro ao excluir questão', 'error');
        }
      }
    }
  };

  const handleCopy = async (id) => {
    try {
      const questionToCopy = questions.find(q => q.id === id);
      if (!questionToCopy) return;

      const newQuestion = {
        text: `${questionToCopy.text} (Cópia)`,
        options: questionToCopy.options,
        is_final: questionToCopy.is_final,
        final_message: questionToCopy.final_message,
        article_reference: questionToCopy.article_reference,
        dynamic_text: questionToCopy.dynamic_text,
        is_success: questionToCopy.is_success
      };

      await questionBankService.create(newQuestion);
      showNotification('Questão copiada com sucesso', 'success');
      fetchQuestions();
    } catch (error) {
      console.error('Erro ao copiar questão:', error);
      showNotification('Erro ao copiar questão', 'error');
    }
  };

  const handleSelectQuestion = (id) => {
    setSelectedQuestions(prev => {
      if (prev.includes(id)) {
        return prev.filter(questionId => questionId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllQuestions = (event) => {
    if (event.target.checked) {
      setSelectedQuestions(questions.map(q => q.id));
    } else {
      setSelectedQuestions([]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedQuestions.length === 0) return;
    
    if (window.confirm(`Tem certeza que deseja excluir ${selectedQuestions.length} questões selecionadas?`)) {
      try {
        const deletePromises = selectedQuestions.map(id => questionBankService.delete(id));
        await Promise.all(deletePromises);
        showNotification('Questões excluídas com sucesso', 'success');
        setSelectedQuestions([]);
        fetchQuestions();
      } catch (error) {
        console.error('Erro ao excluir questões:', error);
        showNotification('Erro ao excluir questões. Algumas questões podem estar em uso em fluxos.', 'error');
      }
    }
  };

  const getFilteredQuestions = () => {
    switch (activeListTab) {
      case 0: // Todas as questões
        return questions.filter(q => !q.is_final);
      case 1: // Conclusões positivas
        return questions.filter(q => q.is_final && q.is_success === true);
      case 2: // Conclusões negativas
        return questions.filter(q => q.is_final && q.is_success === false);
      default:
        return questions.filter(q => !q.is_final);
    }
  };

  // Função para ordenar as questões
  const handleSort = (column) => {
    const isAsc = orderBy === column && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  // Função para comparar valores na ordenação
  const compareValues = (a, b, column) => {
    if (column === 'flow_count') {
      return orderDirection === 'asc' ? a[column] - b[column] : b[column] - a[column];
    }
    
    const valueA = (a[column] || '').toString().toLowerCase();
    const valueB = (b[column] || '').toString().toLowerCase();
    
    if (orderDirection === 'asc') {
      return valueA.localeCompare(valueB);
    }
    return valueB.localeCompare(valueA);
  };

  // Função para filtrar e ordenar as questões
  const getFilteredAndSortedQuestions = () => {
    let filtered = getFilteredQuestions();
    
    // Aplicar busca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        q.text.toLowerCase().includes(searchLower) ||
        (q.final_message && q.final_message.toLowerCase().includes(searchLower)) ||
        (q.article_reference && q.article_reference.toLowerCase().includes(searchLower)) ||
        (q.options && q.options.some(opt => opt.toLowerCase().includes(searchLower)))
      );
    }
    
    // Aplicar ordenação
    return filtered.sort((a, b) => compareValues(a, b, orderBy));
  };

  const getQuestionsCount = () => {
    const questionsCount = questions.filter(q => !q.is_final).length;
    const positiveCount = questions.filter(q => q.is_final && q.is_success === true).length;
    const negativeCount = questions.filter(q => q.is_final && q.is_success === false).length;
    return { questionsCount, positiveCount, negativeCount };
  };

  // Função para renderizar os nomes dos fluxos
  const renderFlowNames = (flows) => {
    if (!flows || flows.length === 0) {
      return <Chip label="Não utilizado" variant="outlined" />;
    }

    // Agrupar fluxos por flow_id e contar ocorrências
    const flowCounts = flows.reduce((acc, flow) => {
      const flowId = flow.flow_id;
      if (!acc[flowId]) {
        acc[flowId] = {
          flow_name: flow.flow_name,
          count: 0
        };
      }
      acc[flowId].count += 1;
      return acc;
    }, {});

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {Object.entries(flowCounts).map(([flowId, { flow_name, count }]) => (
          <Tooltip 
            key={flowId} 
            title={`${flow_name} - Usado ${count} ${count === 1 ? 'vez' : 'vezes'} neste fluxo. Clique para filtrar.`}
            arrow
          >
            <Chip 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      maxWidth: '150px', 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    {flow_name}
                  </Typography>
                  {count > 1 && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        bgcolor: 'rgba(0,0,0,0.08)', 
                        px: 0.5, 
                        py: 0.2, 
                        borderRadius: '10px',
                        fontWeight: 'bold'
                      }}
                    >
                      {count}x
                    </Typography>
                  )}
                </Box>
              }
              color="info"
              size="small"
              onClick={() => setSelectedFlowId(flowId)}
              sx={{ 
                maxWidth: '100%',
                '& .MuiChip-label': { 
                  px: 1,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center'
                } 
              }}
            />
          </Tooltip>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Banco de Questões
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
              onClick={() => {
                setCurrentQuestion({
                  text: '',
                  options: [],
                  is_final: false,
                  final_message: '',
                  article_reference: '',
                  dynamic_text: null,
                  is_success: null
                });
                setOpenDialog(true);
              }}
          >
            Nova Questão
          </Button>
            <Button 
              variant="contained" 
              color="secondary" 
              startIcon={<AddIcon />}
              onClick={() => {
                setCurrentQuestion({
                  text: '',
                  options: [],
                  is_final: true,
                  final_message: '',
                  article_reference: '',
                  dynamic_text: null,
                  is_success: null
                });
                setOpenConclusionDialog(true);
              }}
            >
              Nova Conclusão
            </Button>
          </Box>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={activeListTab} 
            onChange={(e, newValue) => {
              setActiveListTab(newValue);
              setSelectedQuestions([]);
            }}
            aria-label="tipos de questões"
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Questões
                  <Chip 
                    label={getQuestionsCount().questionsCount} 
                    size="small" 
                    color={activeListTab === 0 ? "primary" : "default"}
                    sx={{ 
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Conclusões Positivas
                  <Chip 
                    label={getQuestionsCount().positiveCount} 
                    size="small"
                    color={activeListTab === 1 ? "success" : "default"}
                    sx={{ 
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Conclusões Negativas
                  <Chip 
                    label={getQuestionsCount().negativeCount} 
                    size="small"
                    color={activeListTab === 2 ? "error" : "default"}
                    sx={{ 
                      height: '20px',
                      '& .MuiChip-label': {
                        px: 1,
                        fontSize: '0.75rem'
                      }
                    }}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por texto, mensagem final, referência ou opções..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'background.paper' }}
            InputProps={{
              startAdornment: (
                <Box sx={{ color: 'text.secondary', mr: 1 }}>
                  <SearchIcon />
                </Box>
              ),
            }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="flow-filter-label">Filtrar por Fluxo</InputLabel>
            <Select
              labelId="flow-filter-label"
              value={selectedFlowId}
              onChange={(e) => setSelectedFlowId(e.target.value)}
              label="Filtrar por Fluxo"
              startAdornment={
                <Box sx={{ color: 'text.secondary', mr: 1 }}>
                  <FilterListIcon />
                </Box>
              }
            >
              <MenuItem value="">Todos os Fluxos</MenuItem>
              {flows.map((flow) => (
                <MenuItem key={flow.id} value={flow.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="body2">{flow.name}</Typography>
                    <Chip 
                      label={flow.questionCount} 
                      size="small" 
                      color="primary" 
                      sx={{ ml: 1, height: '20px', '& .MuiChip-label': { px: 1 } }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {selectedQuestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                >
                  Excluir {selectedQuestions.length} questões selecionadas
                </Button>
              </Box>
            )}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedQuestions.length > 0 && selectedQuestions.length < getFilteredAndSortedQuestions().length}
                        checked={getFilteredAndSortedQuestions().length > 0 && selectedQuestions.length === getFilteredAndSortedQuestions().length}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedQuestions(getFilteredAndSortedQuestions().map(q => q.id));
                          } else {
                            setSelectedQuestions([]);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleSort('id')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        ID
                        {orderBy === 'id' && (
                          <Box component="span" sx={{ ml: 1 }}>
                            {orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell 
                      sx={{ minWidth: 400, cursor: 'pointer' }}
                      onClick={() => handleSort('text')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Conteúdo
                        {orderBy === 'text' && (
                          <Box component="span" sx={{ ml: 1 }}>
                            {orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell 
                      sx={{ cursor: 'pointer', minWidth: 150 }}
                      onClick={() => handleSort('flow_count')}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Fluxos
                        {orderBy === 'flow_count' && (
                          <Box component="span" sx={{ ml: 1 }}>
                            {orderDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredAndSortedQuestions().map((question) => (
                    <TableRow key={question.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedQuestions.includes(question.id)}
                          onChange={() => handleSelectQuestion(question.id)}
                        />
                      </TableCell>
                      <TableCell>{question.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Typography sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                            {question.text}
                          </Typography>

                          {question.is_final && question.final_message && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Mensagem Final:
                              </Typography>
                              <Typography sx={{ whiteSpace: 'pre-wrap', ml: 2 }}>
                                {question.final_message}
                              </Typography>
                            </Box>
                          )}

                          {!question.is_final && question.options && question.options.length > 0 && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Opções:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 2 }}>
                                {question.options.map((option, index) => (
                                  <Chip
                                    key={index}
                                    label={option}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {question.article_reference && (
                            <Box>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Referência:
                              </Typography>
                              <Typography sx={{ ml: 2 }}>
                                {question.article_reference}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {question.is_final ? (
                          <Chip 
                            label={question.is_success ? "Conclusão Positiva" : "Conclusão Negativa"}
                            color={question.is_success ? "success" : "error"}
                            icon={question.is_success ? <SuccessIcon /> : <FailIcon />}
                          />
                        ) : (
                          <Chip label="Pergunta" color="primary" />
                        )}
                      </TableCell>
                      <TableCell>
                        {renderFlowNames(question.flows)}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <IconButton onClick={() => handleOpenDialog(question)} color="primary">
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleCopy(question.id)} color="info">
                            <CopyIcon />
                          </IconButton>
                          <IconButton 
                            onClick={() => handleDelete(question.id)} 
                            color="error"
                            disabled={question.flow_count > 0}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      {/* Diálogo para criar/editar questão */}
      <QuestionForm
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmit}
        question={currentQuestion}
        isNew={!currentQuestion}
      />

      {/* Diálogo para criar/editar conclusão */}
      <ConclusionForm
        open={openConclusionDialog}
        onClose={handleCloseDialog}
        onSave={handleSubmit}
        question={currentQuestion}
        isNew={!currentQuestion}
      />
    </Container>
  );
}; 